/**
 * SimulatorTab - Simulador de conversación REALISTA
 * Prueba el Smart Flow con el agente de IA real
 * Permite simular clientes nuevos y existentes
 * Diseño: light theme matching AdminCorrections pattern
 */

import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  SmartToy as BotIcon,
  Person as UserIcon,
  Psychology as ClassifyIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  PersonAdd as NewClientIcon,
  Person as ExistingClientIcon,
  Build as ToolIcon,
  Storage as KBIcon,
  Memory as MemoryIcon,
} from "@mui/icons-material";

const API_URL = "/api";

const CLASSIFICATION_COLORS = {
  FLUJO: "#6366f1",
  TOOL_USE: "#22c55e",
  SINTOMAS: "#f59e0b",
  MEMORIA: "#8b5cf6",
  INFORMACION: "#3b82f6",
  CONVERSACION: "#64748b",
};

// Icono para síntomas (corregir import)
function PersonAddSvg(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
    </svg>
  );
}

const CLASSIFICATION_ICONS = {
  FLUJO: <BotIcon sx={{ fontSize: 14 }} />,
  TOOL_USE: <ToolIcon sx={{ fontSize: 14 }} />,
  SINTOMAS: <PersonAddSvg sx={{ fontSize: 14 }} />,
  MEMORIA: <MemoryIcon sx={{ fontSize: 14 }} />,
  INFORMACION: <KBIcon sx={{ fontSize: 14 }} />,
  CONVERSACION: <UserIcon sx={{ fontSize: 14 }} />,
};

// Card limpia estilo Pacientes/SmartFlowsList (sin glassmorphism, bordes sólidos)
const cardSx = {
  borderRadius: 2.5,
  background: "#fff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
  position: "relative",
  overflow: "hidden",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    color: "#1e293b",
    fontSize: 13,
    borderRadius: 2,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { color: "#64748b", fontSize: 12, "&.Mui-focused": { color: "#1e3a5f" } },
};

export default function SimulatorTab({ chatbotId, chatbotName, agentConfig }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionVars, setSessionVars] = useState({});
  const [showConfig, setShowConfig] = useState(true);

  // Ref para mantener el valor más reciente de sessionVars (evita problemas de closure)
  const sessionVarsRef = useRef(sessionVars);
  useEffect(() => {
    sessionVarsRef.current = sessionVars;
    console.log("[SIMULATOR] sessionVarsRef updated to:", sessionVars);
  }, [sessionVars]);

  // Configuración del cliente simulado
  const [clientConfig, setClientConfig] = useState({
    is_existing: false,
    nombre: "María García",
    telefono: "+57 300 123 4567",
    email: "maria@email.com",
    tiene_citas_previas: false,
    tiene_historial_memoria: false,
  });

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");

    // Agregar mensaje del usuario
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);

    setLoading(true);

    // Usar ref para obtener el valor más reciente (evita stale closure)
    const currentSessionVars = sessionVarsRef.current;

    // DEBUG: Log what we're sending
    console.log("[SIMULATOR] Sending message:", userMessage);
    console.log("[SIMULATOR] Session vars being sent:", currentSessionVars);

    try {
      const requestBody = {
        message: userMessage,
        conversation_history: messages.map((m) => ({
          role: m.role,
          content: m.content,
        })),
        client: clientConfig,
        session_variables: currentSessionVars,
      };
      console.log("[SIMULATOR] Full request body:", requestBody);

      const response = await fetch(`${API_URL}/smart-flows/${chatbotId}/simulate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error("Error en simulación");

      const data = await response.json();
      console.log("[SIMULATOR] Response received:", data);
      console.log("[SIMULATOR] Session vars from response:", data.session_variables);

      // Actualizar variables de sesión
      if (data.session_variables) {
        console.log("[SIMULATOR] Updating sessionVars to:", data.session_variables);
        sessionVarsRef.current = data.session_variables; // Actualizar ref inmediatamente
        setSessionVars(data.session_variables);
      }

      // Agregar respuesta del bot con metadata completa
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response,
          classification: data.classification,
          confidence: data.confidence,
          details: data.details,
          tool_used: data.tool_used,
          tools_executed: data.tools_executed || [],
          kb_source: data.kb_source,
          kb_content_used: data.kb_content_used,
          agent_reasoning: data.agent_reasoning,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Error procesando mensaje: " + err.message,
          classification: "ERROR",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setSessionVars({});
    sessionVarsRef.current = {}; // Reset ref también
  };

  const toggleClientType = () => {
    setClientConfig((prev) => ({
      ...prev,
      is_existing: !prev.is_existing,
      tiene_citas_previas: !prev.is_existing,
      tiene_historial_memoria: !prev.is_existing,
    }));
  };

  return (
    <Box sx={{ display: "flex", gap: 2.5, height: "calc(100vh - 280px)", minHeight: 500 }}>
      {/* Chat Area */}
      <Box
        sx={{
          ...cardSx,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "linear-gradient(90deg, #1e3a5f, #2d5a87)",
          },
        }}
      >
        {/* Chat Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8fafc",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "linear-gradient(135deg, #1e3a5f, #2d5a87)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(30, 58, 95, 0.3)",
                "& svg": { fontSize: 20 },
              }}
            >
              <BotIcon />
            </Box>
            <Box>
              <Typography sx={{ color: "#1e3a5f", fontWeight: 700, fontSize: 14 }}>
                Simulador Realista
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: 11 }}>
                Conectado al agente IA
              </Typography>
            </Box>
            <Chip
              size="small"
              icon={clientConfig.is_existing ? <ExistingClientIcon sx={{ fontSize: 14 }} /> : <NewClientIcon sx={{ fontSize: 14 }} />}
              label={clientConfig.is_existing ? "Cliente Existente" : "Cliente Nuevo"}
              sx={{
                bgcolor: clientConfig.is_existing ? "#dcfce7" : "#ede9fe",
                color: clientConfig.is_existing ? "#16a34a" : "#7c3aed",
                fontSize: 11,
                fontWeight: 600,
                border: clientConfig.is_existing ? "1px solid #bbf7d0" : "1px solid #ddd6fe",
                "& .MuiChip-icon": { color: clientConfig.is_existing ? "#16a34a" : "#7c3aed" },
              }}
            />
          </Box>
          <Tooltip title="Limpiar conversación">
            <IconButton onClick={clearConversation} sx={{ color: "#94a3b8", "&:hover": { color: "#64748b", bgcolor: "#f1f5f9" } }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            bgcolor: "#fafbfc",
          }}
        >
          {messages.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Decorative circle */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #e0e7ff, #dbeafe)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  boxShadow: "0 8px 24px rgba(30, 58, 95, 0.10)",
                }}
              >
                <BotIcon sx={{ fontSize: 40, color: "#1e3a5f" }} />
              </Box>
              <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: 15 }}>
                Simulador de conversación realista
              </Typography>
              <Typography sx={{ fontSize: 12, mt: 0.5, textAlign: "center", color: "#94a3b8" }}>
                Configura el tipo de cliente y prueba diferentes escenarios
              </Typography>
            </Box>
          ) : (
            messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <Box sx={{ maxWidth: "85%", display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexDirection: msg.role === "user" ? "row-reverse" : "row",
                    }}
                  >
                    {/* Avatar */}
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: msg.role === "user"
                          ? "linear-gradient(135deg, #1e3a5f, #2d5a87)"
                          : "linear-gradient(135deg, #6366f1, #4f46e5)",
                        boxShadow: msg.role === "user"
                          ? "0 2px 8px rgba(30, 58, 95, 0.3)"
                          : "0 2px 8px rgba(99, 102, 241, 0.3)",
                        flexShrink: 0,
                      }}
                    >
                      {msg.role === "user" ? (
                        <UserIcon sx={{ fontSize: 16, color: "#fff" }} />
                      ) : (
                        <BotIcon sx={{ fontSize: 16, color: "#fff" }} />
                      )}
                    </Box>
                    {/* Bubble */}
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: msg.role === "user" ? "#1e3a5f" : "#fff",
                        border: msg.role === "user" ? "none" : "1px solid #e2e8f0",
                        boxShadow: msg.role === "user"
                          ? "0 2px 8px rgba(30, 58, 95, 0.25)"
                          : "0 1px 4px rgba(0,0,0,0.06)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: msg.role === "user" ? "#fff" : "#1e293b",
                          fontSize: 13,
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.5,
                        }}
                      >
                        {msg.content}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Metadata para mensajes del bot */}
                  {msg.role === "assistant" && msg.classification && (
                    <Box sx={{ ml: 5, mt: 0.5 }}>
                      {/* Clasificación y confianza */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Chip
                          icon={<ClassifyIcon sx={{ fontSize: 12 }} />}
                          label={`${msg.classification} ${msg.confidence ? `(${Math.round(msg.confidence * 100)}%)` : ""}`}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: 10,
                            fontWeight: 600,
                            bgcolor: `${CLASSIFICATION_COLORS[msg.classification] || "#64748b"}15`,
                            color: CLASSIFICATION_COLORS[msg.classification] || "#64748b",
                            border: `1px solid ${CLASSIFICATION_COLORS[msg.classification] || "#64748b"}30`,
                            "& .MuiChip-icon": { color: CLASSIFICATION_COLORS[msg.classification] || "#64748b" },
                          }}
                        />
                        {msg.tools_executed && msg.tools_executed.length > 0 && (
                          <Chip
                            icon={<ToolIcon sx={{ fontSize: 12 }} />}
                            label={msg.tools_executed.join(", ")}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: 10,
                              fontWeight: 600,
                              bgcolor: "#dcfce7",
                              color: "#16a34a",
                              border: "1px solid #bbf7d0",
                              "& .MuiChip-icon": { color: "#16a34a" },
                            }}
                          />
                        )}
                        {msg.kb_source && (
                          <Chip
                            icon={<KBIcon sx={{ fontSize: 12 }} />}
                            label={`KB: ${msg.kb_source}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: 10,
                              fontWeight: 600,
                              bgcolor: "#dbeafe",
                              color: "#2563eb",
                              border: "1px solid #bfdbfe",
                              "& .MuiChip-icon": { color: "#2563eb" },
                            }}
                          />
                        )}
                      </Box>

                      {/* Razonamiento del agente */}
                      {msg.agent_reasoning && (
                        <Typography
                          sx={{
                            color: "#94a3b8",
                            fontSize: 10,
                            mt: 0.5,
                            fontStyle: "italic",
                          }}
                        >
                          {msg.agent_reasoning}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            ))
          )}
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 5 }}>
              <CircularProgress size={16} sx={{ color: "#1e3a5f" }} />
              <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                Procesando con IA...
              </Typography>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input area */}
        <Box
          sx={{
            p: 1.5,
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            gap: 1,
            bgcolor: "#f8fafc",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Escribe un mensaje..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            sx={inputSx}
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            sx={{
              minWidth: 50,
              borderRadius: 2,
              background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
              boxShadow: "0 4px 12px rgba(30, 58, 95, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)",
                boxShadow: "0 6px 16px rgba(30, 58, 95, 0.4)",
              },
            }}
          >
            <SendIcon sx={{ fontSize: 20 }} />
          </Button>
        </Box>
      </Box>

      {/* Sidebar - Configuración */}
      <Box
        sx={{
          ...cardSx,
          width: 300,
          flexShrink: 0,
          overflowY: "auto",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "linear-gradient(90deg, #1e3a5f, #2d5a87)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Cliente Simulado */}
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                mb: 1.5,
                py: 0.5,
                borderRadius: 1,
                "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
              }}
              onClick={() => setShowConfig(!showConfig)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    background: "linear-gradient(135deg, #f59e0b, #d97706)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    boxShadow: "0 4px 12px rgba(245, 158, 11, 0.4)",
                    "& svg": { fontSize: 16 },
                  }}
                >
                  <UserIcon />
                </Box>
                <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: 13 }}>
                  Cliente Simulado
                </Typography>
              </Box>
              {showConfig ? (
                <CollapseIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
              ) : (
                <ExpandIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
              )}
            </Box>

            <Collapse in={showConfig}>
              {/* Toggle cliente nuevo/existente */}
              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: clientConfig.is_existing ? "#f0fdf4" : "#f5f3ff",
                  border: clientConfig.is_existing
                    ? "1px solid #bbf7d0"
                    : "1px solid #ddd6fe",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={clientConfig.is_existing}
                      onChange={toggleClientType}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#16a34a" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#16a34a",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: "#1e293b", fontSize: 13, fontWeight: 500 }}>
                      {clientConfig.is_existing ? "Cliente Existente" : "Cliente Nuevo"}
                    </Typography>
                  }
                />
                <Typography sx={{ color: "#64748b", fontSize: 11, mt: 0.5 }}>
                  {clientConfig.is_existing
                    ? "Tiene historial y citas previas"
                    : "Primera vez que contacta"}
                </Typography>
              </Box>

              {/* Datos del cliente */}
              <TextField
                fullWidth
                size="small"
                label="Nombre"
                value={clientConfig.nombre}
                onChange={(e) => setClientConfig({ ...clientConfig, nombre: e.target.value })}
                sx={{ mb: 1.5, ...inputSx }}
              />
              <TextField
                fullWidth
                size="small"
                label="Teléfono"
                value={clientConfig.telefono}
                onChange={(e) => setClientConfig({ ...clientConfig, telefono: e.target.value })}
                sx={{ mb: 1.5, ...inputSx }}
              />

              {/* Opciones adicionales para cliente existente */}
              {clientConfig.is_existing && (
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={clientConfig.tiene_citas_previas}
                        onChange={(e) =>
                          setClientConfig({ ...clientConfig, tiene_citas_previas: e.target.checked })
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#16a34a" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#16a34a" },
                        }}
                      />
                    }
                    label={<Typography sx={{ color: "#1e293b", fontSize: 12 }}>Tiene citas previas</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={clientConfig.tiene_historial_memoria}
                        onChange={(e) =>
                          setClientConfig({ ...clientConfig, tiene_historial_memoria: e.target.checked })
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#16a34a" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#16a34a" },
                        }}
                      />
                    }
                    label={<Typography sx={{ color: "#1e293b", fontSize: 12 }}>Tiene historial de memoria</Typography>}
                  />
                </Box>
              )}
            </Collapse>
          </Box>

          {/* Divider */}
          <Box sx={{ borderBottom: "1px solid #e2e8f0", my: 2 }} />

          {/* Clasificaciones */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(99, 102, 241, 0.4)",
                "& svg": { fontSize: 16 },
              }}
            >
              <ClassifyIcon />
            </Box>
            <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: 13 }}>
              Clasificaciones
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 0.5 }}>
            {Object.entries(CLASSIFICATION_COLORS).map(([key, color]) => (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: color,
                    boxShadow: `0 0 6px ${color}40`,
                  }}
                />
                <Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 500 }}>
                  {key}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Divider */}
          <Box sx={{ borderBottom: "1px solid #e2e8f0", my: 2 }} />

          {/* Escenarios de Prueba */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(14, 165, 233, 0.4)",
                "& svg": { fontSize: 16 },
              }}
            >
              <SendIcon />
            </Box>
            <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: 13 }}>
              Escenarios de Prueba
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {[
              { msg: "Hola", label: "Saludo", color: "#6366f1" },
              { msg: "Quiero agendar una cita", label: "Cita", color: "#22c55e" },
              { msg: "Cuánto cuesta la consulta?", label: "Info", color: "#3b82f6" },
              { msg: "Me duele mucho la cabeza", label: "Síntomas", color: "#f59e0b" },
              { msg: "Qué me recomendaste la vez pasada?", label: "Memoria", color: "#8b5cf6" },
              { msg: "Gracias, hasta luego", label: "Despedida", color: "#64748b" },
            ].map(({ msg, label, color }) => (
              <Button
                key={msg}
                size="small"
                variant="outlined"
                onClick={() => setInput(msg)}
                sx={{
                  justifyContent: "space-between",
                  color: "#475569",
                  borderColor: "#e2e8f0",
                  textTransform: "none",
                  fontSize: 11,
                  py: 0.5,
                  px: 1.5,
                  borderRadius: 1.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: color,
                    bgcolor: `${color}08`,
                    transform: "translateX(4px)",
                    color: "#1e293b",
                  },
                }}
              >
                <span>{msg}</span>
                <Chip
                  label={label}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 9,
                    fontWeight: 600,
                    bgcolor: `${color}12`,
                    color: color,
                    border: `1px solid ${color}30`,
                  }}
                />
              </Button>
            ))}
          </Box>

          {/* Variables de Sesión */}
          {Object.keys(sessionVars).length > 0 && (
            <>
              <Box sx={{ borderBottom: "1px solid #e2e8f0", my: 2 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    background: "linear-gradient(135deg, #a855f7, #7c3aed)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    boxShadow: "0 4px 12px rgba(168, 85, 247, 0.4)",
                    "& svg": { fontSize: 16 },
                  }}
                >
                  <MemoryIcon />
                </Box>
                <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: 13 }}>
                  Variables de Sesión
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#f8fafc",
                  fontSize: 10,
                  fontFamily: "monospace",
                  color: "#475569",
                  border: "1px solid #e2e8f0",
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                {JSON.stringify(sessionVars, null, 2)}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
